// Cart Animation module

document.addEventListener('DOMContentLoaded', function() {
  // Initialize the animation
  const cartAnimation = {
    // Flags to track state
    isUpdating: false,
    lastUpdateTime: 0,
    _isFetchingCart: false,
    _updateCartInfoTimeout: null,
    _queuedFetch: null,
    _currentOperations: new Set(), // Track operations by ID
    _operationCounter: 0, // Counter for generating unique operation IDs
    initialized: true, // Flag to indicate initialization is complete
    _animationInProgress: false, // Flag to track if a +1 animation is in progress
    
    init: function() {
      // Reset all state variables
      this.isUpdating = false;
      this.lastUpdateTime = 0;
      this._isFetchingCart = false;
      this._currentOperations.clear();
      this._operationCounter = 0;
      this._animationInProgress = false;
      
      // Clear any pending timeouts
      if (this._updateCartInfoTimeout) {
        clearTimeout(this._updateCartInfoTimeout);
        this._updateCartInfoTimeout = null;
      }
      
      if (this._queuedFetch) {
        clearTimeout(this._queuedFetch);
        this._queuedFetch = null;
      }
      
      // Listen for add to cart events
      this.setupEventListeners();
      
      // Listen for Shopify's variant change events
      this.setupVariantChangeListener();
      
      // Initial sync of quantity controls with cart
      // Use a slight delay to ensure everything is set up
      setTimeout(() => {
        this.updateCartInfo();
        
        // Setup variant card visibility observer AFTER initial sync
        // This ensures forms exist before we start observing
        this.setupVariantObserver();
      }, 500);
    },

    // Generate a unique operation ID
    _getOperationId: function() {
      return `operation_${++this._operationCounter}`;
    },
    
    // Check if the operation is already in progress
    _isOperationInProgress: function(variantId, newQty) {
      const operationKey = `${variantId}_${newQty}`;
      return this._currentOperations.has(operationKey);
    },
    
    // Add operation to tracking
    _trackOperation: function(variantId, newQty) {
      const operationKey = `${variantId}_${newQty}`;
      this._currentOperations.add(operationKey);
      return operationKey;
    },
    
    // Remove operation from tracking
    _completeOperation: function(operationKey) {
      this._currentOperations.delete(operationKey);
    },

    // Queue cart updates for batching rapid clicks
    // This allows users to click +++ quickly and have all clicks register
    _pendingUpdates: {},
    _updateTimers: {},
    
    _queueCartUpdate: function(variantId, delta, button, type) {
      // Initialize pending update for this variant if not exists
      if (!this._pendingUpdates[variantId]) {
        this._pendingUpdates[variantId] = { delta: 0, button: null, type: null };
      }
      
      // Accumulate the delta (can be +1 or -1)
      this._pendingUpdates[variantId].delta += delta;
      this._pendingUpdates[variantId].button = button;
      this._pendingUpdates[variantId].type = type;
      
      // Clear existing timer for this variant
      if (this._updateTimers[variantId]) {
        clearTimeout(this._updateTimers[variantId]);
      }
      
      // Set a short debounce (300ms) to batch rapid clicks
      this._updateTimers[variantId] = setTimeout(() => {
        this._processPendingUpdate(variantId);
      }, 300);
    },
    
    _processPendingUpdate: function(variantId) {
      const pending = this._pendingUpdates[variantId];
      if (!pending || pending.delta === 0) {
        delete this._pendingUpdates[variantId];
        return;
      }
      
      const delta = pending.delta;
      const button = pending.button;
      const type = pending.type;
      
      // Clear the pending update
      delete this._pendingUpdates[variantId];
      delete this._updateTimers[variantId];
      
      // Find the form and input for this variant
      const variantForm = document.querySelector(`form input[name="id"][value="${variantId}"]`)?.closest('form');
      const quantityInput = variantForm?.querySelector('.quantity-input');
      
      // Get the optimistically updated value from the input (or calculate from cart)
      if (quantityInput && quantityInput.value) {
        // Use the optimistically set value
        const newQty = parseInt(quantityInput.value) || 0;
        
        // Update the cart with optimistic value — no .catch() since it returns void
        this.updateCartItemQuantity(variantId, newQty, null);
        
        // Clear original value after initiating update
        if (quantityInput.dataset.originalValue) {
          delete quantityInput.dataset.originalValue;
        }
        
        // Force a sync after update completes to ensure all forms show correct state
        setTimeout(() => {
          this.updateCartInfo();
        }, 100);
      } else {
        // Fallback: fetch cart and calculate
        fetch('/cart.js')
          .then(response => response.json())
          .then(cart => {
            const cartItem = cart.items.find(item => item.variant_id.toString() === variantId.toString());
            const currentQty = cartItem ? cartItem.quantity : 0;
            const newQty = Math.max(0, currentQty + delta);
            
            if (quantityInput) {
              quantityInput.value = newQty;
            }
            
            this.updateCartItemQuantity(variantId, newQty, null);
          })
          .catch(error => {
            console.error('Error processing batched cart update:', error);
          });
      }
    },

    // Show a brief toast/notification message
    showToast: function(message, duration) {
      duration = duration || 3000;
      
      // Remove any existing toast
      const existing = document.querySelector('.cart-toast');
      if (existing) existing.remove();
      
      const toast = document.createElement('div');
      toast.className = 'cart-toast';
      toast.textContent = message;
      document.body.appendChild(toast);
      
      // Animate in
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          toast.classList.add('cart-toast--visible');
        });
      });
      
      // Animate out and remove
      setTimeout(() => {
        toast.classList.remove('cart-toast--visible');
        setTimeout(() => {
          if (toast.parentNode) toast.parentNode.removeChild(toast);
        }, 300);
      }, duration);
    },

    // Helper: get the enforced max inventory for a form/input pair
    // Returns 0 if no limit should be enforced
    _getMaxInventory: function(form, quantityInput) {
      // Check for hidden max_quantity input with data attributes (variant-custom-view forms)
      const maxQtyInput = form.querySelector('input[name="max_quantity"]');
      if (maxQtyInput) {
        const inventoryManagement = maxQtyInput.dataset.inventoryManagement;
        const inventoryPolicy = maxQtyInput.dataset.inventoryPolicy;
        const inventoryQuantity = parseInt(maxQtyInput.dataset.inventoryQuantity) || 0;
        if (inventoryManagement && inventoryPolicy === 'deny' && inventoryQuantity > 0) {
          return inventoryQuantity;
        }
        return 0;
      }
      
      // Fallback: check data attributes on the form itself
      const formInventory = parseInt(form.dataset.inventoryQuantity) || 0;
      const formPolicy = form.dataset.inventoryPolicy || '';
      const formManagement = form.dataset.inventoryManagement || '';
      if (formManagement && formPolicy === 'deny' && formInventory > 0) {
        return formInventory;
      }
      
      // Last fallback: use the input's max attribute
      const inputMax = parseInt(quantityInput.getAttribute('max'));
      if (!isNaN(inputMax) && inputMax > 0) {
        return inputMax;
      }
      
      return 0; // No limit
    },

    setupEventListeners: function() {
      // Prevent double registration of event listeners
      if (this._eventListenersSetup) {
        return;
      }
      this._eventListenersSetup = true;
      
      // Intercept all form submissions for adding to cart
      // Use capture phase to intercept before other handlers
      document.addEventListener('submit', function(event) {
        const form = event.target;
        
        // Check if this is an add to cart form
        if (form && form instanceof HTMLFormElement && form.action && form.action.indexOf('/cart/add') !== -1) {
          // ALWAYS prevent default and stop propagation immediately
          event.preventDefault();
          event.stopPropagation();
          event.stopImmediatePropagation();
          
          // Check if this event was already handled
          if (event._cartAnimationHandled) {
            return;
          }
          event._cartAnimationHandled = true;
          
          // Check if form is already being processed
          if (form.dataset.submitting === 'true') {
            return;
          }
          
          // SKIP out-of-stock variants - they should not allow add to cart
          const submitButton = form.querySelector('button[type="submit"], input[type="submit"]');
          if (submitButton && (submitButton.classList.contains('out-of-stock') || submitButton.disabled)) {
            return;
          }
          
          // Additional check: ensure variant ID exists and is valid
          const variantIdInput = form.querySelector('input[name="id"]');
          if (!variantIdInput || !variantIdInput.value) {
            return;
          }
          
          // Throttle form submissions to prevent double adds (2000ms for safety)
          const formId = form.id || 'cart-form';
          const now = Date.now();
          if (!cartAnimation._lastFormSubmitTime) cartAnimation._lastFormSubmitTime = {};
          
          if (cartAnimation._lastFormSubmitTime[formId] && 
              now - cartAnimation._lastFormSubmitTime[formId] < 2000) {
            return;
          }
          cartAnimation._lastFormSubmitTime[formId] = now;
          
          // Mark form as submitting
          form.dataset.submitting = 'true';
          
          // Get the button that was clicked
          const button = form.querySelector('button[type="submit"], input[type="submit"]');
          
          // Disable button to prevent double clicks
          if (button) {
            button.disabled = true;
          }
          
          // Store button position for animation - always do this on submission
          if (button && typeof button === 'object' && typeof button.getBoundingClientRect === 'function') {
            const rect = button.getBoundingClientRect();
            form.dataset.buttonX = rect.left + rect.width / 2;
            form.dataset.buttonY = rect.top + rect.height / 2;
          }
          
          // Add the item to cart via AJAX
          cartAnimation.addToCartViaAjax(form, button);
          
          // Reset submitting flag after a delay
          setTimeout(() => {
            form.dataset.submitting = 'false';
          }, 2000);
        }
      }, true); // Use capture phase

      // Handle quantity controls button clicks (regular product forms)
      // Use capture phase (true) to run BEFORE buy-buttons.liquid's handler
      document.addEventListener('click', function(event) {
        const target = event.target;
        if (!target) return;
        
        // Handle plus button clicks (regular product forms)
        if (target.classList.contains('plus-btn') || target.closest('.plus-btn')) {
          // Stop event IMMEDIATELY to prevent buy-buttons.liquid handler from firing
          event.preventDefault();
          event.stopPropagation();
          event.stopImmediatePropagation();
          
          const plusBtn = target.classList.contains('plus-btn') ? target : target.closest('.plus-btn');
          
          // Check if this event was already handled (prevents double processing)
          if (event._cartAnimationHandled) {
            return;
          }
          event._cartAnimationHandled = true;
          
          // Use per-button throttle instead of global blocking
          const now = Date.now();
          const buttonId = plusBtn.id || 'plus-btn';
          if (!cartAnimation._buttonLastClick) cartAnimation._buttonLastClick = {};
          
          // Short throttle (200ms) to allow fast clicking while preventing accidental double-clicks
          if (cartAnimation._buttonLastClick[buttonId] && 
              now - cartAnimation._buttonLastClick[buttonId] < 200) {
            return;
          }
          cartAnimation._buttonLastClick[buttonId] = now;
          
          const quantityControls = plusBtn.closest('.quantity-controls');
          
          if (quantityControls) {
            const form = quantityControls.closest('form');
            if (!form || !(form instanceof HTMLFormElement)) return;
            
            const quantityInput = quantityControls.querySelector('input[type="number"]');
            const variantIdInput = form ? form.querySelector('input[name="id"]') : null;
            
            if (form && variantIdInput && quantityInput) {
              const variantId = variantIdInput.value;
              if (!variantId) return;
              
              // Disable button briefly while we fetch cart state
              plusBtn.disabled = true;
              
              // Fetch current cart state, then check max before incrementing
              fetch('/cart.js')
                .then(response => response.json())
                .then(cart => {
                  const cartItem = cart.items.find(item => item.variant_id.toString() === variantId.toString());
                  const currentCartQty = cartItem ? cartItem.quantity : 0;
                  const newValue = currentCartQty + 1;
                  
                  // Check max inventory limit
                  const maxInventory = cartAnimation._getMaxInventory(form, quantityInput);
                  const hasLimit = maxInventory > 0;
                  
                  if (hasLimit && newValue > maxInventory) {
                    // Already at max — show toast and keep button disabled
                    plusBtn.classList.add('at-max-inventory');
                    cartAnimation.showToast(`Only ${maxInventory} available in stock.`);
                    return;
                  }
                  
                  // Update input immediately for instant feedback
                  quantityInput.value = newValue;
                  
                  // Store original value for error rollback
                  quantityInput.dataset.originalValue = currentCartQty;
                  
                  // Queue the cart update in background (pass null so _processPendingUpdate
                  // doesn't try to re-enable — we handle that here)
                  cartAnimation._queueCartUpdate(variantId, 1, null, 'increment');
                  
                  // Re-enable button unless we just hit the max
                  if (hasLimit && newValue >= maxInventory) {
                    plusBtn.disabled = true;
                    plusBtn.classList.add('at-max-inventory');
                    cartAnimation.showToast(`Only ${maxInventory} available in stock.`);
                  } else {
                    plusBtn.disabled = false;
                    plusBtn.classList.remove('at-max-inventory');
                  }
                })
                .catch(error => {
                  console.error('Error fetching cart for optimistic update:', error);
                  plusBtn.disabled = false;
                });
            }
          }
        }
        
        // Handle minus button clicks (regular product forms)
        if (target.classList.contains('minus-btn') || target.closest('.minus-btn')) {
          // Stop event IMMEDIATELY to prevent buy-buttons.liquid handler from firing
          event.preventDefault();
          event.stopPropagation();
          event.stopImmediatePropagation();
          
          const minusBtn = target.classList.contains('minus-btn') ? target : target.closest('.minus-btn');
          
          // Check if this event was already handled (prevents double processing)
          if (event._cartAnimationHandled) {
            return;
          }
          event._cartAnimationHandled = true;
          
          // Use per-button throttle instead of global blocking
          const now = Date.now();
          const buttonId = minusBtn.id || 'minus-btn';
          if (!cartAnimation._buttonLastClick) cartAnimation._buttonLastClick = {};
          
          // Short throttle (200ms) to allow fast clicking while preventing accidental double-clicks
          if (cartAnimation._buttonLastClick[buttonId] && 
              now - cartAnimation._buttonLastClick[buttonId] < 200) {
            return;
          }
          cartAnimation._buttonLastClick[buttonId] = now;
          
          const quantityControls = minusBtn.closest('.quantity-controls');
          
          if (quantityControls) {
            const form = quantityControls.closest('form');
            if (!form || !(form instanceof HTMLFormElement)) return;
            
            const quantityInput = quantityControls.querySelector('input[type="number"]');
            const variantIdInput = form ? form.querySelector('input[name="id"]') : null;
            
            if (form && variantIdInput && quantityInput) {
              const variantId = variantIdInput.value;
              if (!variantId) return;
              
              // Disable button during update
              minusBtn.disabled = true;
              
              // OPTIMISTIC UPDATE: Fetch current cart state, then decrement from cart value
              fetch('/cart.js')
                .then(response => response.json())
                .then(cart => {
                  const cartItem = cart.items.find(item => item.variant_id.toString() === variantId.toString());
                  const currentCartQty = cartItem ? cartItem.quantity : 0;
                  const newValue = Math.max(0, currentCartQty - 1);
                  
                  // Update input immediately for instant feedback
                  quantityInput.value = newValue;
                  
                  // Store original value for error rollback
                  quantityInput.dataset.originalValue = currentCartQty;
                  
                  // If quantity becomes 0, immediately show Add to Cart button
                  if (newValue === 0) {
                    const addToCartBtn = form.querySelector('.add-to-cart-btn');
                    if (addToCartBtn) {
                      addToCartBtn.style.display = 'block';
                      addToCartBtn.disabled = false;
                      addToCartBtn.classList.remove('disabled');
                      quantityControls.style.display = 'none';
                    }
                  }
                  
                  // Re-enable button and clear at-max state (decrementing removes max limit)
                  minusBtn.disabled = false;
                  
                  // Also re-enable plus button if it was at max
                  const plusBtn = quantityControls.querySelector('.plus-btn');
                  if (plusBtn) {
                    plusBtn.disabled = false;
                    plusBtn.classList.remove('at-max-inventory');
                  }
                  
                  // Queue the cart update in background
                  cartAnimation._queueCartUpdate(variantId, -1, null, 'decrement');
                })
                .catch(error => {
                  console.error('Error fetching cart for optimistic update:', error);
                  minusBtn.disabled = false;
                });
            }
          }
        }

        // Handle cart page quantity controls - Plus button
        if (target.classList.contains('quantity__button') && target.name === 'plus') {
          // Stop event immediately to prevent other handlers
          event.preventDefault();
          event.stopPropagation();
          event.stopImmediatePropagation();
          
          const plusBtn = target;
          
          // Use per-button throttle (200ms)
          const now = Date.now();
          const buttonKey = 'cart-plus-' + (plusBtn.dataset.id || 'btn');
          if (!cartAnimation._buttonLastClick) cartAnimation._buttonLastClick = {};
          
          if (cartAnimation._buttonLastClick[buttonKey] && 
              now - cartAnimation._buttonLastClick[buttonKey] < 200) {
            return;
          }
          cartAnimation._buttonLastClick[buttonKey] = now;
          
          // Check if this event was already handled
          if (event._cartAnimationHandled) {
            return;
          }
          event._cartAnimationHandled = true;
          
          const quantityInputEl = plusBtn.closest('quantity-input');
          if (!quantityInputEl) return;
          
          const quantityInput = quantityInputEl.querySelector('input[type="number"]');
          if (!quantityInput) return;
          
          const variantKey = quantityInput.dataset.id;
          if (!variantKey) return;
          
          // Get current quantity and increment by 1
          const currentQty = parseInt(quantityInput.value) || 0;
          const newQty = currentQty + 1;
          
          // Check if this exact operation is already in progress
          if (cartAnimation._isOperationInProgress(variantKey, newQty)) {
            return;
          }
          
          // Get the line index and key from the input
          const lineIndex = quantityInput.dataset.index;
          const itemKey = quantityInput.dataset.id;
          
          if (!lineIndex || !itemKey) {
            console.error('Missing line index or item key for cart update');
            return;
          }
          
          // Find the cart-items element and use its updateQuantity method
          const cartItemsElement = document.querySelector('cart-items');
          if (!cartItemsElement) {
            console.error('cart-items element not found');
            return;
          }
          
          // Check if update is already in progress for this item
          if (cartAnimation.isUpdating) {
            return;
          }
          
          // Track the operation
          const operationKey = cartAnimation._trackOperation(variantKey, newQty);
          
          // Mark as updating
          cartAnimation.isUpdating = true;
          cartAnimation.lastUpdateTime = now;
          
          // Disable button while updating
          plusBtn.disabled = true;
          
          // Use cart.js's updateQuantity method to ensure single update mechanism
          const observer = new MutationObserver((mutations) => {
            const updatedInput = document.querySelector(`input[data-id="${itemKey}"][data-index="${lineIndex}"]`);
            if (updatedInput && parseInt(updatedInput.value) === newQty) {
              cartAnimation._completeOperation(operationKey);
              if (cartAnimation._currentOperations.size === 0) {
                cartAnimation.isUpdating = false;
              }
              plusBtn.disabled = false;
              observer.disconnect();
            }
          });
          
          observer.observe(cartItemsElement, { childList: true, subtree: true, characterData: true });
          
          cartItemsElement.updateQuantity(lineIndex, itemKey, newQty, 'updates[]', quantityInput);
          
          event.preventDefault();
          event.stopPropagation();
        }
        
        // Handle cart page quantity controls - Minus button
        if (target.classList.contains('quantity__button') && target.name === 'minus') {
          // Stop event immediately to prevent other handlers
          event.preventDefault();
          event.stopPropagation();
          event.stopImmediatePropagation();
          
          const minusBtn = target;
          
          // Use per-button throttle (200ms)
          const now = Date.now();
          const buttonKey = 'cart-minus-' + (minusBtn.dataset.id || 'btn');
          if (!cartAnimation._buttonLastClick) cartAnimation._buttonLastClick = {};
          
          if (cartAnimation._buttonLastClick[buttonKey] && 
              now - cartAnimation._buttonLastClick[buttonKey] < 200) {
            return;
          }
          cartAnimation._buttonLastClick[buttonKey] = now;
          
          // Check if this event was already handled
          if (event._cartAnimationHandled) {
            return;
          }
          event._cartAnimationHandled = true;
          
          const quantityInputEl = minusBtn.closest('quantity-input');
          if (!quantityInputEl) return;
          
          const quantityInput = quantityInputEl.querySelector('input[type="number"]');
          if (!quantityInput) return;
          
          const variantKey = quantityInput.dataset.id;
          if (!variantKey) return;
          
          // Get current quantity and decrement by 1
          const currentQty = parseInt(quantityInput.value) || 0;
          const newQty = Math.max(0, currentQty - 1);
          
          // Check if this exact operation is already in progress
          if (cartAnimation._isOperationInProgress(variantKey, newQty)) {
            return;
          }
          
          // Get the line index and key from the input
          const lineIndex = quantityInput.dataset.index;
          const itemKey = quantityInput.dataset.id;
          
          if (!lineIndex || !itemKey) {
            console.error('Missing line index or item key for cart update');
            return;
          }
          
          // Find the cart-items element and use its updateQuantity method
          const cartItemsElement = document.querySelector('cart-items');
          if (!cartItemsElement) {
            console.error('cart-items element not found');
            return;
          }
          
          // Check if update is already in progress for this item
          if (cartAnimation.isUpdating) {
            return;
          }
          
          // Track the operation
          const operationKey = cartAnimation._trackOperation(variantKey, newQty);
          
          // Mark as updating
          cartAnimation.isUpdating = true;
          cartAnimation.lastUpdateTime = now;
          
          // Disable button while updating
          minusBtn.disabled = true;
          
          const observer = new MutationObserver((mutations) => {
            const updatedInput = document.querySelector(`input[data-id="${itemKey}"][data-index="${lineIndex}"]`);
            if (updatedInput && parseInt(updatedInput.value) === newQty) {
              cartAnimation._completeOperation(operationKey);
              if (cartAnimation._currentOperations.size === 0) {
                cartAnimation.isUpdating = false;
              }
              minusBtn.disabled = false;
              observer.disconnect();
            }
          });
          
          observer.observe(cartItemsElement, { childList: true, subtree: true, characterData: true });
          
          cartItemsElement.updateQuantity(lineIndex, itemKey, newQty, 'updates[]', quantityInput);
          
          event.preventDefault();
          event.stopPropagation();
        }
      }, true); // Use capture phase to intercept events BEFORE buy-buttons.liquid
      
      // Handle direct changes to quantity inputs
      document.addEventListener('change', function(event) {
        const target = event.target;
        if (!target) return;
        
        // Check if this is a quantity input in the quantity controls (regular product forms)
        if (target.classList.contains('quantity-input') && target.closest('.quantity-controls')) {
          // Prevent rapid consecutive updates
          if (cartAnimation.isUpdating) {
            return;
          }
          
          const quantityControls = target.closest('.quantity-controls');
          const form = quantityControls.closest('form');
          if (!form || !(form instanceof HTMLFormElement)) return;
          
          const variantIdInput = form ? form.querySelector('input[name="id"]') : null;
          
          if (form && variantIdInput) {
            const variantId = variantIdInput.value;
            if (!variantId) return;
            
            const newQty = parseInt(target.value) || 0;
            
            // Check if this exact operation is already in progress
            if (cartAnimation._isOperationInProgress(variantId, newQty)) {
              return;
            }
            
            // If quantity becomes 0, show the Add to Cart button again
            if (newQty === 0) {
              const addToCartBtn = form.querySelector('.add-to-cart-btn');
              if (addToCartBtn) {
                addToCartBtn.style.display = 'block';
                addToCartBtn.disabled = false;
                addToCartBtn.classList.remove('disabled');
                quantityControls.style.display = 'none';
              }
            }
            
            // Update the cart
            cartAnimation.lastUpdateTime = Date.now();
            cartAnimation.updateCartItemQuantity(variantId, newQty, null);
          }
        }
        
        // Check if this is a quantity input in the cart page
        if (target.classList.contains('quantity-input') && target.closest('quantity-input') && target.name === 'updates[]') {
          // Prevent rapid consecutive updates
          if (cartAnimation.isUpdating) {
            return;
          }
          
          const quantityInputEl = target.closest('quantity-input');
          if (!quantityInputEl) return;
          
          const variantKey = target.dataset.id;
          if (!variantKey) return;
          
          const newQty = parseInt(target.value) || 0;
          
          // Check if this exact operation is already in progress
          if (cartAnimation._isOperationInProgress(variantKey, newQty)) {
            return;
          }
          
          // Track the operation
          const operationKey = cartAnimation._trackOperation(variantKey, newQty);
          
          // Mark as updating
          cartAnimation.isUpdating = true;
          cartAnimation.lastUpdateTime = Date.now();
          
          // Update cart via AJAX
          cartAnimation.updateCartItemQuantityByKey(variantKey, newQty, null, operationKey);
        }
      });
    },

    addToCartViaAjax: function(form, button) {
      // Validate form before proceeding
      if (!form || !(form instanceof HTMLFormElement)) {
        console.error('Invalid form element for FormData construction');
        form.dataset.submitting = 'false';
        if (button) button.disabled = false;
        return;
      }
      
      // Double-check we haven't already submitted this form recently
      if (form.dataset.ajaxInProgress === 'true') {
        return;
      }
      form.dataset.ajaxInProgress = 'true';
      
      const formData = new FormData(form);
      
      // Store the original button in a data attribute on the form for reliable reference
      if (button && typeof button === 'object' && typeof button.getBoundingClientRect === 'function') {
        const rect = button.getBoundingClientRect();
        form.dataset.buttonX = rect.left + rect.width / 2;
        form.dataset.buttonY = rect.top + rect.height / 2;
      }
      
      fetch('/cart/add.js', {
        method: 'POST',
        body: formData
      })
      .then(response => response.json())
      .then(data => {
        // Immediately show quantity controls with the quantity from the response
        const addToCartBtn = form.querySelector('.add-to-cart-btn') || form.querySelector('button[type="submit"], input[type="submit"]');
        const quantityControls = form.querySelector('.quantity-controls');
        
        if (addToCartBtn && quantityControls && data.quantity) {
          // Re-enable and hide add button
          addToCartBtn.disabled = false;
          addToCartBtn.style.display = 'none';
          
          // Show quantity controls with the actual quantity from cart response
          quantityControls.style.display = 'flex';
          
          const quantityInput = quantityControls.querySelector('input[type="number"]');
          if (quantityInput) {
            quantityInput.value = data.quantity;
          }
        }
        
        // Update cart count and total in background
        cartAnimation.updateCartInfo();

        // On the cart page, refresh line items + Don't Forget These without a full reload
        if (
          document.getElementById('main-cart-items') &&
          typeof window.CartDontForget?.refreshCartPageSections === 'function'
        ) {
          window.CartDontForget.refreshCartPageSections();
        }
        
        // Reset form state after successful submission
        form.dataset.ajaxInProgress = 'false';
        form.dataset.submitting = 'false';
      })
      .catch(error => {
        console.error('Error adding product to cart:', error);
        form.dataset.ajaxInProgress = 'false';
        form.dataset.submitting = 'false';
        if (button) button.disabled = false;
      });
    },

    // New method to trigger animation from coordinates when we don't have a direct element reference
    triggerAnimationFromCoordinates: function(x, y) {
      // Create a temporary placeholder element at the specified coordinates
      const placeholder = document.createElement('div');
      placeholder.style.position = 'fixed';
      placeholder.style.left = `${x - 1}px`;
      placeholder.style.top = `${y - 1}px`;
      placeholder.style.width = '2px';
      placeholder.style.height = '2px';
      placeholder.style.pointerEvents = 'none';
      placeholder.style.opacity = '0';
      
      document.body.appendChild(placeholder);
      this.triggerAnimation(placeholder);
      
      setTimeout(() => {
        if (placeholder.parentNode) {
          placeholder.parentNode.removeChild(placeholder);
        }
      }, 2000);
    },

    triggerAnimation: function(sourceElement) {
      if (!sourceElement || typeof sourceElement !== 'object' || typeof sourceElement.getBoundingClientRect !== 'function') {
        return;
      }
      
      this._animationInProgress = true;
      
      const desktopCartIcon = document.querySelector('.tas-header__cart-btn');
      const mobileCartIcon = document.querySelector('.tas-header-mobile__cart');
      
      let cartIcon = null;
      let isMobile = window.innerWidth < 768;
      
      if (isMobile && mobileCartIcon) {
        cartIcon = mobileCartIcon;
      } else if (desktopCartIcon) {
        cartIcon = desktopCartIcon;
      } else if (mobileCartIcon) {
        cartIcon = mobileCartIcon;
      }
      
      if (!cartIcon) {
        this._animationInProgress = false;
        return;
      }
      
      const sourceRect = sourceElement.getBoundingClientRect();
      const cartRect = cartIcon.getBoundingClientRect();
      
      const bubble = document.createElement('div');
      bubble.className = 'cart-animation-bubble';
      bubble.textContent = '+1';
      
      bubble.style.position = 'fixed';
      bubble.style.zIndex = '1000';
      bubble.style.backgroundColor = '#14854e';
      bubble.style.color = 'white';
      bubble.style.borderRadius = '50%';
      bubble.style.width = '40px';
      bubble.style.height = '40px';
      bubble.style.display = 'flex';
      bubble.style.alignItems = 'center';
      bubble.style.justifyContent = 'center';
      bubble.style.fontWeight = 'bold';
      bubble.style.fontSize = '20px';
      bubble.style.pointerEvents = 'none';
      bubble.style.transition = 'all 800ms ease-in-out';
      bubble.style.opacity = '0';
      bubble.style.transform = 'scale(0.5)';
      bubble.style.left = `${sourceRect.left + sourceRect.width / 2 - 20}px`;
      bubble.style.top = `${sourceRect.top + sourceRect.height / 2 - 20}px`;
      
      document.body.appendChild(bubble);
      
      setTimeout(() => {
        bubble.style.opacity = '1';
        bubble.style.transform = 'scale(1)';
      }, 10);
      
      setTimeout(() => {
        bubble.style.left = `${cartRect.left + cartRect.width / 2 - 20}px`;
        bubble.style.top = `${cartRect.top + cartRect.height / 2 - 20}px`;
        bubble.style.transform = 'scale(0.7)';
      }, 200);
      
      setTimeout(() => {
        bubble.style.opacity = '0';
        
        cartIcon.classList.add('pulse-animation');
        
        const mobileCartCount = document.querySelector('.tas-header-mobile__cart-count');
        if (mobileCartCount) {
          mobileCartCount.classList.add('pulse-animation');
          setTimeout(() => {
            mobileCartCount.classList.remove('pulse-animation');
          }, 800);
        }
        
        const cartCount = document.querySelector('.tas-header__cart-count');
        if (cartCount) {
          cartCount.classList.add('pulse-animation');
          setTimeout(() => {
            cartCount.classList.remove('pulse-animation');
          }, 800);
        }
        
        const cartTotal = document.querySelector('.tas-header__cart-total');
        if (cartTotal) {
          cartTotal.classList.add('pulse-animation');
          setTimeout(() => {
            cartTotal.classList.remove('pulse-animation');
          }, 800);
        }
        
        setTimeout(() => {
          cartIcon.classList.remove('pulse-animation');
        }, 800);
        
        setTimeout(() => {
          if (bubble.parentNode) {
            bubble.parentNode.removeChild(bubble);
          }
          this._animationInProgress = false;
        }, 300);
      }, 1000);
    },

    updateCartInfo: function() {
      if (this._updateCartInfoTimeout) {
        clearTimeout(this._updateCartInfoTimeout);
      }
      
      const delayTime = this._animationInProgress ? 800 : 300;
      
      this._updateCartInfoTimeout = setTimeout(() => {
        this._fetchCartData();
      }, delayTime);
    },
    
    _fetchCartData: function() {
      if (this._isFetchingCart) {
        if (!this._queuedFetch) {
          this._queuedFetch = setTimeout(() => {
            this._queuedFetch = null;
            this._fetchCartData();
          }, 500);
        }
        return;
      }
      
      this._isFetchingCart = true;
      
      fetch('/cart.json')
        .then(res => {
          const contentType = res.headers.get('Content-Type') || '';
          if (!res.ok || !contentType.includes('application/json')) {
            return Promise.reject(new Error('Cart endpoint did not return JSON'));
          }
          return res.json();
        })
        .then(cart => {
          this._isFetchingCart = false;
          
          if (cart.item_count !== undefined) {
            const updateWithTransition = (element, newValue, propName = 'textContent') => {
              if (!element) return;
              
              if (element[propName] !== newValue) {
                element.classList.add('cart-value-updating');
                
                setTimeout(() => {
                  element[propName] = newValue;
                  
                  setTimeout(() => {
                    element.classList.remove('cart-value-updating');
                  }, 300);
                }, 100);
              } else {
                element[propName] = newValue;
              }
            };
            
            document.querySelectorAll(".cart-count").forEach(el => {
              if (el.classList.contains("cart-count-drawer")) {
                updateWithTransition(el, `(${cart.item_count})`, 'innerHTML');
              } else {
                updateWithTransition(el, cart.item_count > 100 ? '~' : cart.item_count, 'innerHTML');
              }
            });
            
            const mobileCartCount = document.querySelector('.tas-header-mobile__cart-count');
            if (mobileCartCount) {
              updateWithTransition(mobileCartCount, cart.item_count);
              
              if (!this._animationInProgress) {
                mobileCartCount.classList.add('pulse-animation');
                setTimeout(() => {
                  mobileCartCount.classList.remove('pulse-animation');
                }, 800);
              }
            }
            
            const cartCount = document.querySelector('.tas-header__cart-count');
            if (cartCount) {
              const itemsText = (window.theme && window.theme.strings && window.theme.strings.general && window.theme.strings.general.cart && window.theme.strings.general.cart.items) 
                ? window.theme.strings.general.cart.items.toUpperCase() 
                : 'ITEMS';
              updateWithTransition(cartCount, `${cart.item_count} ${itemsText}`);
              
              if (!this._animationInProgress) {
                cartCount.classList.add('pulse-animation');
                setTimeout(() => {
                  cartCount.classList.remove('pulse-animation');
                }, 800);
              }
            }
            
            const cartTotal = document.querySelector('.tas-header__cart-total');
            if (cartTotal) {
              updateWithTransition(cartTotal, formatMoney(cart.total_price));
              
              if (!this._animationInProgress) {
                cartTotal.classList.add('pulse-animation');
                setTimeout(() => {
                  cartTotal.classList.remove('pulse-animation');
                }, 800);
              }
            }
            
            if (document.querySelector("header-total-price")) {
              document.querySelector("header-total-price").updateTotal(cart);
            }
            
            const cart_free_ship = document.querySelector("free-ship-progress-bar");
            if (cart_free_ship) {
              cart_free_ship.init(cart.items_subtotal_price);
            }
            
            this.updateQuantityControls(cart.items);
          }
        })
        .catch(() => {
          this._isFetchingCart = false;
        });
    },
    
    updateQuantityControls: function(cartItems) {
      if (!cartItems || !Array.isArray(cartItems)) return;
      
      if (this.isUpdating) {
        return;
      }
      
      try {
        const processedVariants = new Set();
        
        const forms = document.querySelectorAll('.add-to-cart-form, form[data-type="add-to-cart-form"]');
        
        forms.forEach(form => {
          if (!form || !(form instanceof HTMLFormElement)) return;
          
          const variantIdInput = form.querySelector('input[name="id"]');
          if (!variantIdInput) return;
          
          const variantId = parseInt(variantIdInput.value);
          if (!variantId) {
            return;
          }
          
          if (processedVariants.has(variantId)) {
            return;
          }
          
          processedVariants.add(variantId);
          
          const addToCartBtn = form.querySelector('.add-to-cart-btn') || form.querySelector('button[type="submit"], input[type="submit"]');
          const quantityControls = form.querySelector('.quantity-controls');
          
          if (!addToCartBtn) {
            return;
          }
          
          if (!quantityControls) {
            return;
          }
          
          // SKIP out-of-stock variants
          if (addToCartBtn.classList.contains('out-of-stock') || addToCartBtn.disabled) {
            return;
          }
          
          const cartItem = cartItems.find(item => item.variant_id === variantId);
          
          if (cartItem) {
            const quantityInput = quantityControls.querySelector('input[type="number"]');
            const inCartQty = cartItem.quantity;
            
            addToCartBtn.style.display = 'none';
            quantityControls.style.display = 'flex';
            
            if (quantityInput) {
              quantityInput.value = inCartQty;
            }
            
            // Update plus button disabled state based on max inventory
            const plusBtn = quantityControls.querySelector('.plus-btn');
            if (plusBtn && quantityInput) {
              const maxInventory = cartAnimation._getMaxInventory(form, quantityInput);
              if (maxInventory > 0 && inCartQty >= maxInventory) {
                plusBtn.disabled = true;
                plusBtn.classList.add('at-max-inventory');
              } else {
                plusBtn.disabled = false;
                plusBtn.classList.remove('at-max-inventory');
              }
            }
          } else {
            addToCartBtn.style.display = 'block';
            addToCartBtn.disabled = false;
            addToCartBtn.classList.remove('disabled');
            quantityControls.style.display = 'none';
            
            const quantityInput = quantityControls.querySelector('input[type="number"]');
            if (quantityInput) {
              quantityInput.value = 1;
            }
            
            // Reset plus button state
            const plusBtn = quantityControls.querySelector('.plus-btn');
            if (plusBtn) {
              plusBtn.disabled = false;
              plusBtn.classList.remove('at-max-inventory');
            }
          }
        });
      } catch (error) {
        console.error('Error in updateQuantityControls:', error);
      }
    },
    
    isElementVisible: function(element) {
      if (!element) return false;
      
      const style = window.getComputedStyle(element);
      if (style.display === 'none' || style.visibility === 'hidden') {
        return false;
      }
      
      const rect = element.getBoundingClientRect();
      return (
        rect.top >= 0 &&
        rect.left >= 0 &&
        rect.bottom <= (window.innerHeight || document.documentElement.clientHeight) &&
        rect.right <= (window.innerWidth || document.documentElement.clientWidth)
      );
    },

    updateCartItemQuantity: function(variantId, quantity, button) {
      if (!variantId) {
        console.error('Invalid variant ID for cart update');
        return;
      }
      
      const operationKey = this._trackOperation(variantId, quantity);
      
      this.isUpdating = true;
      
      if (button) button.disabled = true;
      
      fetch('/cart/update.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          updates: {
            [variantId]: quantity
          }
        })
      })
      .then(response => response.json())
      .then(data => {
        const updatedItem = data.items.find(item => item.variant_id.toString() === variantId.toString());
        if (updatedItem && updatedItem.quantity !== quantity) {
          // Shopify capped the quantity (e.g. exceeds inventory)
          document.querySelectorAll('.add-to-cart-form').forEach(form => {
            const variantIdInput = form.querySelector('input[name="id"]');
            if (variantIdInput && variantIdInput.value === variantId.toString()) {
              const quantityInput = form.querySelector('.quantity-controls input[type="number"]');
              if (quantityInput) {
                quantityInput.value = updatedItem.quantity;
              }
            }
          });
        }
        
        this._completeOperation(operationKey);
        this.updateCartInfo();
        document.dispatchEvent(new CustomEvent('cart:updated'));
        
        setTimeout(() => {
          if (this._currentOperations.size === 0) {
            this.isUpdating = false;
          }
        }, 500);
      })
      .catch(error => {
        console.error('Error updating cart:', error);
        this._completeOperation(operationKey);
        if (button) button.disabled = false;
        if (this._currentOperations.size === 0) {
          this.isUpdating = false;
        }
      });
    },

    updateCartItemQuantityByKey: function(variantKey, quantity, button, operationKey) {
      if (!variantKey) {
        console.error('Invalid variant key for cart update');
        return;
      }
      
      operationKey = operationKey || this._trackOperation(variantKey, quantity);
      
      this.isUpdating = true;
      
      if (button) button.disabled = true;
      
      fetch('/cart/update.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          updates: {
            [variantKey]: quantity
          }
        })
      })
      .then(response => response.json())
      .then(data => {
        const updatedItem = data.items.find(item => item.variant_id.toString() === variantKey.toString());
        if (updatedItem && updatedItem.quantity !== quantity) {
          document.querySelectorAll('.add-to-cart-form').forEach(form => {
            const variantIdInput = form.querySelector('input[name="id"]');
            if (variantIdInput && variantIdInput.value === variantKey.toString()) {
              const quantityInput = form.querySelector('.quantity-controls input[type="number"]');
              if (quantityInput) {
                quantityInput.value = updatedItem.quantity;
              }
            }
          });
        }
        
        this._completeOperation(operationKey);
        this.updateCartInfo();
        if (button) button.disabled = false;
        document.dispatchEvent(new CustomEvent('cart:updated'));
        
        setTimeout(() => {
          if (this._currentOperations.size === 0) {
            this.isUpdating = false;
          }
        }, 500);
      })
      .catch(error => {
        console.error('Error updating cart:', error);
        this._completeOperation(operationKey);
        if (button) button.disabled = false;
        if (this._currentOperations.size === 0) {
          this.isUpdating = false;
        }
      });
    },
    
    setupVariantChangeListener: function() {
      const variantEvents = ['variant:changed', 'shopify:variant:change', 'variantChange'];
      
      variantEvents.forEach(eventName => {
        document.addEventListener(eventName, (event) => {
          fetch('/cart.js')
            .then(response => response.json())
            .then(cart => {
              this.updateQuantityControls(cart.items);
            })
            .catch(error => {
              console.error('[VariantListener] Error syncing after variant change:', error);
            });
        });
      });
    },
    
    setupVariantObserver: function() {
      const syncNow = () => {
        fetch('/cart.js')
          .then(response => response.json())
          .then(cart => {
            this.updateQuantityControls(cart.items);
          })
          .catch(error => {
            console.error('Error syncing variant button states:', error);
          });
      };
      
      let scrollTimeout = null;
      const debouncedScrollSync = () => {
        if (scrollTimeout) clearTimeout(scrollTimeout);
        scrollTimeout = setTimeout(() => {
          syncNow();
        }, 50);
      };
      
      const variantContainer = document.querySelector('.variant-container, .variant-container-wrapper');
      if (variantContainer) {
        variantContainer.addEventListener('scroll', debouncedScrollSync, { passive: true });
        variantContainer.addEventListener('scrollend', syncNow, { passive: true });
      }
      
      window.addEventListener('scroll', debouncedScrollSync, { passive: true });
      
      document.querySelectorAll('.variant-card').forEach(card => {
        card.addEventListener('mouseenter', syncNow, { passive: true });
        card.addEventListener('touchstart', syncNow, { passive: true });
        card.addEventListener('click', syncNow, { passive: true });
      });
      
      if ('IntersectionObserver' in window) {
        const observer = new IntersectionObserver((entries) => {
          entries.forEach(entry => {
            if (entry.isIntersecting) {
              syncNow();
            }
          });
        }, {
          root: null,
          rootMargin: '100px',
          threshold: 0.1
        });
        
        const variantCards = document.querySelectorAll('.variant-card');
        variantCards.forEach(card => observer.observe(card));
      }
      
      // Immediate sync on setup
      syncNow();
    }
  };

  // Helper function to format money values
  function formatMoney(cents) {
    const currencyFormat = document.querySelector('[data-currency-format]');
    let format = currencyFormat ? currencyFormat.dataset.currencyFormat : '${{amount}}';
    
    if (typeof cents === 'string') {
      cents = cents.replace('.', '');
    }
    
    const placeholderRegex = /\{\{\s*(\w+)\s*\}\}/;
    
    function defaultOption(value, defaultValue) {
      return (typeof value === 'undefined') ? defaultValue : value;
    }
    
    function formatWithDelimiters(number, precision, thousands, decimal) {
      precision = defaultOption(precision, 2);
      thousands = defaultOption(thousands, ',');
      decimal = defaultOption(decimal, '.');
      
      if (isNaN(number) || number == null) {
        return 0;
      }
      
      number = (number / 100.0).toFixed(precision);
      
      const parts = number.split('.');
      const dollars = parts[0].replace(/(\d)(?=(\d\d\d)+(?!\d))/g, '$1' + thousands);
      const cents = parts[1] ? (decimal + parts[1]) : '';
      
      return dollars + cents;
    }
    
    let value = '';
    
    try {
      switch (format.match(placeholderRegex)[1]) {
        case 'amount':
          value = formatWithDelimiters(cents, 2);
          break;
        case 'amount_no_decimals':
          value = formatWithDelimiters(cents, 0);
          break;
        case 'amount_with_comma_separator':
          value = formatWithDelimiters(cents, 2, '.', ',');
          break;
        case 'amount_no_decimals_with_comma_separator':
          value = formatWithDelimiters(cents, 0, '.', ',');
          break;
        default:
          value = formatWithDelimiters(cents, 2);
      }
    } catch (error) {
      value = '$' + (cents / 100).toFixed(2);
      return value;
    }
    
    return format.replace(placeholderRegex, value);
  }

  // Add CSS for animations and toast
  const style = document.createElement('style');
  style.textContent = `
    @keyframes pulse {
      0% { transform: scale(1); }
      50% { transform: scale(1.1); }
      100% { transform: scale(1); }
    }
    
    .pulse-animation {
      animation: pulse 0.7s ease-in-out;
    }
    
    .cart-animation-bubble {
      box-shadow: 0 2px 5px rgba(0, 0, 0, 0.2);
      font-size: 16px;
    }
    
    .cart-value-updating {
      transition: opacity 0.3s ease-in-out;
      opacity: 0.7;
    }

    .cart-toast {
      position: fixed;
      bottom: 24px;
      left: 50%;
      transform: translateX(-50%) translateY(20px);
      background: #333;
      color: #fff;
      padding: 12px 24px;
      border-radius: 6px;
      font-size: 14px;
      font-weight: 500;
      z-index: 99999;
      pointer-events: none;
      opacity: 0;
      transition: opacity 0.25s ease, transform 0.25s ease;
      white-space: nowrap;
      box-shadow: 0 4px 12px rgba(0,0,0,0.25);
    }

    .cart-toast--visible {
      opacity: 1;
      transform: translateX(-50%) translateY(0);
    }
  `;
  document.head.appendChild(style);

  // Clean up any existing instance
  if (window.cartAnimation) {
    if (window.cartAnimation._updateCartInfoTimeout) {
      clearTimeout(window.cartAnimation._updateCartInfoTimeout);
    }
    
    if (window.cartAnimation._queuedFetch) {
      clearTimeout(window.cartAnimation._queuedFetch);
    }
    
    window.cartAnimation.isUpdating = false;
    window.cartAnimation._isFetchingCart = false;
  }

  // Initialize the cart animation
  cartAnimation.init();
  
  window.cartAnimation = cartAnimation;
  
  // Ensure cart buttons are properly hyperlinked
  function ensureCartButtonsLinked() {
    const desktopCartBtn = document.querySelector('.tas-header__cart-btn');
    const mobileCartBtn = document.querySelector('.tas-header-mobile__cart');
    
    if (desktopCartBtn && !desktopCartBtn.closest('a')) {
      const cartLink = document.createElement('a');
      cartLink.href = '/cart';
      cartLink.className = 'tas-header__cart-link';
      const parent = desktopCartBtn.parentNode;
      parent.insertBefore(cartLink, desktopCartBtn);
      cartLink.appendChild(desktopCartBtn);
    }
    
    if (mobileCartBtn && !mobileCartBtn.closest('a')) {
      const cartLink = document.createElement('a');
      cartLink.href = '/cart';
      cartLink.className = 'tas-header-mobile__cart-link';
      const parent = mobileCartBtn.parentNode;
      parent.insertBefore(cartLink, mobileCartBtn);
      cartLink.appendChild(mobileCartBtn);
    }
  }
  
  ensureCartButtonsLinked();
  
  if (typeof MutationObserver !== 'undefined') {
    const observer = new MutationObserver(function(mutations) {
      ensureCartButtonsLinked();
    });
    
    observer.observe(document.body, { 
      childList: true,
      subtree: true
    });
  }
  
  window.addEventListener('resize', function() {
    // viewport size change — next animation will use correct cart icon
  });
  
  window.addEventListener('beforeunload', function() {
    if (window.cartAnimation) {
      if (window.cartAnimation._updateCartInfoTimeout) {
        clearTimeout(window.cartAnimation._updateCartInfoTimeout);
      }
      if (window.cartAnimation._queuedFetch) {
        clearTimeout(window.cartAnimation._queuedFetch);
      }
      window.cartAnimation._currentOperations.clear();
      window.cartAnimation.isUpdating = false;
      window.cartAnimation._isFetchingCart = false;
    }
  });

  if (typeof MutationObserver !== 'undefined') {
    const addToCartObserver = new MutationObserver(function(mutations) {
      setupAddToCartButtonsForAnimation();
    });
    
    addToCartObserver.observe(document.body, { 
      childList: true,
      subtree: true
    });
  }
  
  setupAddToCartButtonsForAnimation();
});

// Helper function to properly set up all Add to Cart buttons
// NOTE: This function only sets up position tracking for animations.
// All click handling is done via document-level event delegation in setupEventListeners().
function setupAddToCartButtonsForAnimation() {
  const variantAddToCartForms = document.querySelectorAll('.product-variant__add-to-cart-form');
  variantAddToCartForms.forEach(form => {
    form.classList.add('cart-animation-managed');
    
    const button = form.querySelector('button.add-to-cart-btn');
    if (button && !button.dataset.animationHandlerAdded) {
      button.dataset.animationHandlerAdded = 'true';
      
      const originalOnClick = button.onclick;
      
      button.onclick = function(event) {
        const rect = button.getBoundingClientRect();
        form.dataset.buttonX = rect.left + rect.width / 2;
        form.dataset.buttonY = rect.top + rect.height / 2;
        
        if (typeof originalOnClick === 'function') {
          return originalOnClick.call(this, event);
        }
      };
    }
    
    const plusBtn = form.querySelector('.plus-btn');
    if (plusBtn) {
      plusBtn.dataset.animationHandlerAdded = 'true';
    }
    
    const minusBtn = form.querySelector('.minus-btn');
    if (minusBtn) {
      minusBtn.dataset.animationHandlerAdded = 'true';
    }
  });
  
  const addToCartForms = document.querySelectorAll('.add-to-cart-form:not(.cart-animation-managed)');
  addToCartForms.forEach(form => {
    form.classList.add('cart-animation-managed');
    
    const button = form.querySelector('button.add-to-cart-btn, button[type="submit"], input[type="submit"]');
    if (button && !button.dataset.animationHandlerAdded) {
      button.dataset.animationHandlerAdded = 'true';
    }
  });
}
